import React, { useState, useEffect, ReactElement, useRef } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";

import { ReactComponent as IconArrow } from "../../assets/images/AnswerList/icon_dropdown_arrow.svg";
import { ReactComponent as IconCheckOn } from "../../assets/images/AnswerList/icon_check_on.svg";
import useOnClickOutside from "../../hooks/useOnClickOutside";

import styles from "./DropdownMenu.module.scss";

export type Option = {
  value: number;
  label: string | ReactElement;
  data?: any;
};

const BackDrop = () => {
  return createPortal(<div className={styles.backdrop} />, document.body);
};

export type Props = {
  options: Option[];
  placeholder?: string;
  defaultValue?: Option;
  className?: string;
  classNameOptions?: string;
  fontSize?: "base" | "sm";
  backdrop?: boolean;
  openUpwards?: boolean;
  onChange: (value: number, data?: any) => void;
};

const TEXT_NO_DATA = "データがありません";

export const Dropdown: React.FC<Props> = ({
  options,
  placeholder,
  defaultValue,
  className,
  classNameOptions,
  fontSize = "base",
  backdrop,
  openUpwards,
  onChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [optionSelected, setOptionSelected] = useState<Option>({
    value: -1,
    label: TEXT_NO_DATA,
    data: null,
  });

  useEffect(() => {
    if (
      defaultValue === undefined &&
      optionSelected.value === -1 &&
      options.length !== 0
    ) {
      placeholder
        ? setOptionSelected({ value: -1, label: placeholder })
        : setOptionSelected(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, defaultValue]);

  useEffect(() => {
    if (optionSelected.value !== -1) {
      onChange(optionSelected.value, optionSelected.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionSelected.value]);

  useEffect(() => {
    if (defaultValue) {
      setOptionSelected({
        value: defaultValue.value,
        label: defaultValue.label,
        data: defaultValue.data,
      });
    }
  }, [defaultValue]);

  const labelChange = (option: Option) => {
    if (options.length !== 0)
      if (
        options.filter(o => o.value === option.value).length === 0 &&
        !!options[0]
      ) {
        return placeholder;
      } else return option.label;
    else return TEXT_NO_DATA;
  };

  const handleClickOutside = () => setOpen(false);

  useOnClickOutside(ref, handleClickOutside);

  return (
    <>
      <div
        ref={ref}
        className={clsx(
          styles.wrapSelect,
          backdrop && open && styles.aboveBackdrop,
          className,
        )}
        onClick={() => setOpen(!open)}
      >
        <div className={styles.option}>
          <div
            className={clsx(styles.label, styles.ellipsis, styles[fontSize])}
          >
            {labelChange(optionSelected)}
          </div>
          <IconArrow className={clsx(openUpwards && styles.upwardsArrowIcon)} />
        </div>
        {open && options.length !== 0 && (
          <div
            className={clsx(
              styles.options,
              openUpwards && styles.openUpwards,
              classNameOptions,
            )}
          >
            {options.map(item => (
              <div
                className={clsx(
                  styles.option,
                  item.value === optionSelected.value && styles.isActive,
                )}
                onClick={() => {
                  setOptionSelected(item);
                }}
                key={item.value}
              >
                <div className={clsx(styles.label, styles[fontSize])}>
                  {item.label}
                </div>
                <IconCheckOn className={styles.iconCheck} />
              </div>
            ))}
          </div>
        )}
      </div>
      {backdrop && open && <BackDrop />}
    </>
  );
};
export default Dropdown;
